import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Upload, RefreshCw, X, ZoomIn, ImagePlus } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardContent } from "../ui/Card";
import { useSpaceStore } from "../../stores/spaceStore";
import { loadSpace, getAvailableSurfaces, discardDraft } from "../../services/api";
import { useToast } from "../ui/Toast";
import { formatSurfaceLabelFromRaw } from "../../lib/utils";
import { useSpacesApi } from "../../../../../common/hooks/use-spaces-api.hook";

const ANALYSIS_POLL_MS = 3000;
// The pipeline takes ~10-40 s; a stuck job must not spin forever.
const ANALYSIS_TIMEOUT_MS = 5 * 60 * 1000;
const STATUS_LABEL: Record<string, string> = {
  queued: "Queued",
  processing: "Starting analysis",
  segmentation: "Finding surfaces",
  shading: "Computing lighting",
  geometry: "Computing geometry",
  saving: "Saving space",
};

export function SpaceLoader() {
  const [spaceIdInput, setSpaceIdInput] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [showImageDialog, setShowImageDialog] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { analyzeSpace, getAnalysisStatus } = useSpacesApi();

  const {
    spaceId,
    spaceInfo,
    originalImage,
    surfaces,
    setSpaceId,
    setSpaceInfo,
    setSurfaces,
    setOriginalImage,
    setLoading,
    setError
  } = useSpaceStore();

  const { addToast } = useToast();

  const handleLoadSpace = () => {
    if (!spaceIdInput.trim()) {
      addToast("error", "Please enter a space ID");
      return;
    }
    return loadById(spaceIdInput.trim());
  };

  /**
   * New curated room: run the photo through the same analysis pipeline as a
   * visitor upload (so it gets masks, geometry, shading, measurements and
   * furniture detection like any other space), then open it here. It stays
   * hidden from every vendor until published in the Deploy section.
   */
  const handleUploadNew = async (file: File) => {
    setIsLoading(true);
    setUploadStatus("Uploading");
    try {
      const { jobId } = await analyzeSpace(file);
      const started = Date.now();
      let newSpaceId: string | null = null;
      while (!newSpaceId) {
        if (Date.now() - started > ANALYSIS_TIMEOUT_MS) {
          throw new Error(`Analysis is taking too long (job ${jobId}). Try loading it later by id.`);
        }
        await new Promise((resolve) => setTimeout(resolve, ANALYSIS_POLL_MS));
        const job = await getAnalysisStatus(jobId);
        if (job.status === "failed") throw new Error(job.error || job.message || "Analysis failed");
        if (job.status === "completed") {
          newSpaceId = job.result?.id;
          if (!newSpaceId) throw new Error("Analysis finished but returned no space id");
        } else {
          const label = STATUS_LABEL[job.status] || job.status;
          setUploadStatus(job.progress != null ? `${label} (${job.progress}%)` : label);
        }
      }
      setUploadStatus("Opening");
      setSpaceIdInput(newSpaceId);
      addToast("success", "Space analysed — it stays hidden until you publish it in Deploy");
      await loadById(newSpaceId);
    } catch (error: any) {
      let message = error instanceof Error ? error.message : "Upload failed";
      try {
        const body = await error?.json?.();
        if (body?.message) message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
      } catch { /* not a JSON error */ }
      addToast("error", message);
    } finally {
      setUploadStatus(null);
      setIsLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Opened from the spaces list: /space-manager?space=<id>
  const [searchParams] = useSearchParams();
  const requestedSpace = searchParams.get("space");
  React.useEffect(() => {
    if (requestedSpace && requestedSpace !== spaceId) {
      setSpaceIdInput(requestedSpace);
      loadById(requestedSpace);
    }
  }, [requestedSpace]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/avif"
      className="hidden"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) handleUploadNew(file);
      }}
    />
  );

  // Unpublished edits are shared by every server, so they come back on load;
  // this throws them away and shows the published space again.
  const handleDiscardDraft = async () => {
    if (!spaceId) return;
    if (!window.confirm("Discard all unpublished changes to this space?")) return;
    setIsLoading(true);
    try {
      const response = await discardDraft(spaceId) as any;
      if (!(response.success || response.status === "SUCCESS")) throw new Error(response.message || "Discard failed");
      addToast("success", "Unpublished changes discarded");
    } catch (error) {
      addToast("error", error instanceof Error ? error.message : "Discard failed");
      setIsLoading(false);
      return;
    }
    await loadById(spaceId);
  };

  const loadById = async (id: string) => {
    setIsLoading(true);
    setLoading(true);
    setError(null);

    try {
      // Load space data
      const response = await loadSpace(id) as any;
      console.log("Load space response:", response);

      // API returns { success: true, space: {...}, message: "..." }
      if (response.success || response.status === "SUCCESS") {
        setSpaceId(id);

        // API returns space info in 'space' field
        const spaceData = response.space || response.space_info;
        if (spaceData) {
          setSpaceInfo(spaceData);
        }

        // Load available surfaces from room_data (includes wall_0, wall_1, etc.)
        try {
          const surfacesResponse = await getAvailableSurfaces(id);
          if (surfacesResponse.surfaces && surfacesResponse.surfaces.length > 0) {
            // Convert wall_0, wall_1 format to wall1, wall2 for consistency
            const formattedSurfaces = surfacesResponse.surfaces.map(s => {
              if (s.startsWith('wall_')) {
                const idx = parseInt(s.replace('wall_', ''), 10);
                return `wall${idx + 1}`;
              }
              return s;
            });
            setSurfaces(formattedSurfaces);
          } else if (spaceData?.surfaces) {
            // Fallback to space data surfaces
            setSurfaces(spaceData.surfaces);
          }
        } catch (e) {
          console.warn("Could not load available surfaces:", e);
          // Fallback to space data surfaces
          if (spaceData?.surfaces) {
            setSurfaces(spaceData.surfaces);
          }
        }

        // Set original image from space data (thumbnailPath or uncroppedThumbnailPath)
        const imageUrl = spaceData?.uncroppedThumbnailPath || spaceData?.thumbnailPath;
        if (imageUrl) {
          setOriginalImage(imageUrl);
        }

        addToast("success", `Space loaded: ${spaceData?.name || id}`);
      } else {
        throw new Error(response.message || "Failed to load space");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to load space";
      setError(message);
      addToast("error", message);
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleRefreshSurfaces = async () => {
    if (!spaceId) return;

    try {
      const surfacesResponse = await getAvailableSurfaces(spaceId);
      if (surfacesResponse.surfaces && surfacesResponse.surfaces.length > 0) {
        // Convert wall_0, wall_1 format to wall1, wall2 for consistency
        const formattedSurfaces = surfacesResponse.surfaces.map(s => {
          if (s.startsWith('wall_')) {
            const idx = parseInt(s.replace('wall_', ''), 10);
            return `wall${idx + 1}`;
          }
          return s;
        });
        setSurfaces(formattedSurfaces);
        addToast("success", "Surfaces refreshed");
      }
    } catch (error) {
      addToast("error", "Failed to refresh surfaces");
    }
  };

  return (
    <Card>
      <CardContent className="py-3">
        {/* Compact single-row layout when space is loaded */}
        {spaceInfo ? (
          <div className="flex items-center gap-4">
            {/* Image thumbnail */}
            {originalImage && (
              <button
                onClick={() => setShowImageDialog(true)}
                className="relative h-16 w-24 flex-shrink-0 overflow-hidden rounded bg-muted border hover:ring-2 hover:ring-primary/50 transition-all group"
              >
                <img
                  src={originalImage}
                  alt="Space preview"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <ZoomIn className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            )}

            {/* Space info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold truncate">{spaceInfo.name}</h3>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {spaceInfo.width}x{spaceInfo.height}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {surfaces.map((surface) => (
                  <span
                    key={surface.label}
                    className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                  >
                    {formatSurfaceLabelFromRaw(surface.label)}
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button variant="ghost" size="sm" onClick={handleRefreshSurfaces} title="Refresh surfaces">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <div className="flex gap-1">
                <Input
                  placeholder="Space ID..."
                  value={spaceIdInput}
                  onChange={(e) => setSpaceIdInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLoadSpace()}
                  className="w-48 h-8 text-sm"
                />
                <Button size="sm" onClick={handleLoadSpace} loading={isLoading}>
                  <Search className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isLoading} title="Upload a new space">
                  <ImagePlus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Initial load state - centered input */
          <div className="flex items-center justify-center gap-2 py-2">
            <Upload className="h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Enter space ID..."
              value={spaceIdInput}
              onChange={(e) => setSpaceIdInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLoadSpace()}
              className="max-w-xs"
            />
            <Button onClick={handleLoadSpace} loading={isLoading}>
              <Search className="h-4 w-4 mr-2" />
              Load
            </Button>
            <span className="text-sm text-muted-foreground px-1">or</span>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={isLoading}>
              <ImagePlus className="h-4 w-4 mr-2" />
              Upload new space
            </Button>
          </div>
        )}

        {uploadInput}
        {spaceInfo?.draft && (
          <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm">
            <span>
              Unpublished changes restored
              {spaceInfo.draft.updated_at ? ` (last edit ${new Date(spaceInfo.draft.updated_at * 1000).toLocaleString()})` : ""}.
              Deploy to publish them.
            </span>
            <Button size="sm" variant="destructive" onClick={handleDiscardDraft} disabled={isLoading}>
              Discard changes
            </Button>
          </div>
        )}
        {uploadStatus && (
          <p className="mt-2 text-center text-sm text-muted-foreground">
            {uploadStatus}…
          </p>
        )}

        {/* Image Dialog */}
        {showImageDialog && originalImage && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={() => setShowImageDialog(false)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]">
              <img
                src={originalImage}
                alt="Space preview full"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setShowImageDialog(false)}
                className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
